import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { generateInvoicePdf, generateInvoiceFilename } from '../utils/generateInvoicePdf';
import { getNextInvoiceNumber, loadLogo } from '../lib/contractorSettings';
import { buildCommonPdfLabels } from '../utils/pdfHelpers';
import type { StructuredData } from '../api/types';
import type { ContractorSettings } from '../lib/contractorSettings';

interface DownloadInvoiceButtonProps {
  data: {
    structured_data: StructuredData;
    created_at: string;
    contractor: ContractorSettings;
    vatRate: number | null;
    markupPercent: number | null;
    quoteId?: string;
  };
  className?: string;
}

export function DownloadInvoiceButton({ data, className }: Readonly<DownloadInvoiceButtonProps>) {
  const { t, i18n } = useTranslation();

  const handleDownload = useCallback(() => {
    const invoiceNumber = getNextInvoiceNumber();
    const logo = loadLogo();
    const today = new Date().toISOString().split('T')[0];
    const locale = i18n.language === 'et' ? 'et-EE' : 'en-US';
    const vatRate = data.vatRate;

    const blob = generateInvoicePdf({
      ...data,
      invoiceNumber,
      logo,
      locale,
      labels: {
        notesHeading: t('pdf.notes'),
        invoiceTitle: t('invoice.title'),
        invoiceNumberLabel: t('invoice.number'),
        invoiceDateLabel: t('invoice.date'),
        dueDateLabel: t('invoice.dueDate'),
        quoteRefLabel: t('invoice.quoteRef'),
        paymentInfoHeading: t('invoice.paymentInfo'),
        billTo: t('pdf.billTo'),
        ...buildCommonPdfLabels(t, vatRate),
      },
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = generateInvoiceFilename(invoiceNumber, data.structured_data.client_name, today);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [data, t, i18n.language]);

  return (
    <Button
      variant="outline"
      aria-label={t('invoice.generate')}
      onClick={handleDownload}
      className={cn('touch-target', className)}
    >
      <Receipt className="mr-1 h-4 w-4" />
      {t('invoice.generate')}
    </Button>
  );
}
